import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FaCopy, FaExternalLinkAlt, FaQrcode, FaTimes } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { API_BASE, PUBLIC_BASE_URL } from "../config/constants";

const THEMES = [
  { id: "classic", name: "Classic Amber", desc: "Warm, timeless and trusted.", colors: ["#b45309", "#fde68a"] },
  { id: "minimal", name: "Minimal Mono", desc: "Clean, airy, and modern.", colors: ["#0f172a", "#e2e8f0"] },
  { id: "luxe", name: "Luxe Gold", desc: "Rich, premium vibe.", colors: ["#1b1411", "#fbbf24"] },
  { id: "neon", name: "Neon Pulse", desc: "High energy and bold.", colors: ["#0c1026", "#22d3ee"] },
  { id: "earthy", name: "Earthy Craft", desc: "Natural and cozy.", colors: ["#f3efe4", "#7c5e3b"] },
  { id: "pastel", name: "Pastel Pop", desc: "Soft, friendly palette.", colors: ["#f9f1ff", "#ec4899"] },
  { id: "retro", name: "Retro Spice", desc: "Warm and nostalgic.", colors: ["#fffbeb", "#ea580c"] },
  { id: "cafe", name: "Cafe Roast", desc: "Coffeehouse elegance.", colors: ["#f6f1ea", "#b45309"] },
  { id: "ocean", name: "Ocean Breeze", desc: "Cool, fresh and light.", colors: ["#e0f2fe", "#0284c7"] },
  { id: "street", name: "Street Pop", desc: "Dark, urban edge.", colors: ["#111827", "#f97316"] },
];

const DigitalMenuPage = () => {
  const [restaurantId, setRestaurantId] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState("classic");
  const [menuUrl, setMenuUrl] = useState("");
  const [showQrModal, setShowQrModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedThemeName = useMemo(() => {
    const match = THEMES.find((item) => item.id === selectedTheme);
    return match ? match.name : selectedTheme;
  }, [selectedTheme]);

  const loadProfile = useCallback(async () => {
    const stored = JSON.parse(localStorage.getItem("userData") || "{}");
    const profile = stored?.data || {};
    const rid = profile?.id || null;
    setRestaurantId(rid);
    setMenuUrl(rid ? `${PUBLIC_BASE_URL}/menu/${rid}` : "");

    const theme = String(profile?.menu_theme || "").trim().toLowerCase();
    if (theme) setSelectedTheme(theme);
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const applyTheme = async () => {
    if (!restaurantId) {
      toast.error("Restaurant not found.");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/menu/theme`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: restaurantId, menu_theme: selectedTheme }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || "Theme update failed");
      }
      const stored = JSON.parse(localStorage.getItem("userData") || "{}");
      if (stored?.data) {
        localStorage.setItem(
          "userData",
          JSON.stringify({
            ...stored,
            data: { ...stored.data, menu_theme: selectedTheme },
          })
        );
      }
      toast.success(`Theme applied: ${selectedThemeName}`);
    } catch (error) {
      toast.error(error?.message || "Unable to apply theme");
    } finally {
      setSaving(false);
    }
  };

  const qrUrl = useMemo(() => {
    if (!menuUrl) return "";
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
      menuUrl
    )}`;
  }, [menuUrl]);

  const openMenuLink = useCallback(() => {
    if (!menuUrl) {
      toast.error("Menu link not available.");
      return;
    }
    window.open(menuUrl, "_blank", "noopener,noreferrer");
  }, [menuUrl]);

  const copyMenuLink = useCallback(async () => {
    if (!menuUrl) {
      toast.error("Menu link not available.");
      return;
    }
    try {
      await navigator.clipboard.writeText(menuUrl);
      toast.success("Menu link copied");
    } catch {
      toast.error("Unable to copy link");
    }
  }, [menuUrl]);

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Hero>
        <Badge>Digital Menu</Badge>
        <Title>Digital Menu Theme</Title>
        <Sub>Choose a theme for your QR menu.</Sub>
      </Hero>

      <MenuAccessCard>
        <MenuAccessTop>
          <MenuTitleWrap>
            <MenuTitle>
              <FaQrcode />
              Scanner & Menu Link
            </MenuTitle>
            <MenuSub>Share this QR with customers to open your digital menu.</MenuSub>
          </MenuTitleWrap>
          <MenuActions>
            <LinkBtn type="button" onClick={copyMenuLink}>
              <FaCopy /> Copy
            </LinkBtn>
            <LinkBtn type="button" onClick={openMenuLink}>
              <FaExternalLinkAlt /> Open
            </LinkBtn>
            <LinkBtn type="button" onClick={() => setShowQrModal(true)}>
              <FaQrcode /> QR
            </LinkBtn>
          </MenuActions>
        </MenuAccessTop>
        <MenuUrl>{menuUrl || "Menu link not available"}</MenuUrl>
      </MenuAccessCard>

      <Current>
        Current theme: <strong>{selectedThemeName}</strong>
      </Current>

      <Grid>
        {THEMES.map((theme) => {
          const active = theme.id === selectedTheme;
          return (
            <Card key={theme.id} $active={active} onClick={() => setSelectedTheme(theme.id)}>
              <Preview style={{ background: `linear-gradient(135deg, ${theme.colors[0]}, ${theme.colors[1]})` }} />
              <CardTitle>{theme.name}</CardTitle>
              <CardSub>{theme.desc}</CardSub>
              {active && <ActivePill>Selected</ActivePill>}
            </Card>
          );
        })}
      </Grid>

      <PrimaryBtn disabled={saving} onClick={applyTheme}>
        {saving ? "Saving..." : "Apply Theme"}
      </PrimaryBtn>

      {showQrModal ? (
        <QrModalBackdrop onClick={() => setShowQrModal(false)}>
          <QrModalCard onClick={(e) => e.stopPropagation()}>
            <QrModalHead>
              <h3>Digital Menu QR</h3>
              <button type="button" onClick={() => setShowQrModal(false)}>
                <FaTimes />
              </button>
            </QrModalHead>
            <QrInfo>Scan this QR to open your restaurant menu instantly.</QrInfo>
            {qrUrl ? <QrModalImage src={qrUrl} alt="Digital menu QR code" /> : <MenuUrl>QR not available</MenuUrl>}
            <QrModalActions>
              <LinkBtn type="button" onClick={copyMenuLink}>
                <FaCopy /> Copy Link
              </LinkBtn>
              <LinkBtn type="button" onClick={openMenuLink}>
                <FaExternalLinkAlt /> Open Menu
              </LinkBtn>
            </QrModalActions>
          </QrModalCard>
        </QrModalBackdrop>
      ) : null}
    </Page>
  );
};

export default DigitalMenuPage;

const Page = styled.div`
  min-height: 100%;
  padding: 12px;
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, rgba(1, 91, 154, 1), rgba(0, 57, 98, 1));
  color: #fff;
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow: 0 10px 24px rgba(1, 91, 154, 0.18);
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.25);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-size: 12px;
`;

const Title = styled.h1`
  margin: 8px 0 4px;
  font-size: 1.6rem;
  font-weight: 900;
`;

const Sub = styled.div`
  opacity: 0.9;
`;

const Current = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 16px;
  font-weight: 600;
`;

const MenuAccessCard = styled.div`
  background: linear-gradient(140deg, #ffffff 0%, #f0f9ff 100%);
  border: 1px solid #bfdbfe;
  border-radius: 16px;
  padding: 14px;
  display: grid;
  gap: 12px;
  box-shadow: 0 12px 26px rgba(14, 116, 144, 0.14);
`;

const MenuAccessTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const MenuTitle = styled.div`
  font-weight: 900;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 1.02rem;
`;

const MenuTitleWrap = styled.div`
  display: grid;
  gap: 4px;
`;

const MenuSub = styled.div`
  color: #475569;
  font-size: 12px;
  font-weight: 600;
`;

const MenuActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const LinkBtn = styled.button`
  border: 1px solid rgba(14, 116, 144, 0.28);
  background: linear-gradient(135deg, #ffffff, #e0f2fe);
  color: #0f172a;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: transform 0.16s ease;
  &:hover {
    transform: translateY(-1px);
  }
`;

const MenuUrl = styled.div`
  font-size: 13px;
  color: #0f172a;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  word-break: break-all;
  font-weight: 600;
`;

const QrModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 16px;
`;

const QrModalCard = styled.div`
  width: min(92vw, 460px);
  background: linear-gradient(160deg, #ffffff 0%, #f8fbff 100%);
  border-radius: 18px;
  border: 1px solid #bfdbfe;
  box-shadow: 0 26px 58px rgba(2, 6, 23, 0.36);
  padding: 16px;
`;

const QrModalHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  h3 {
    margin: 0;
    color: #0f172a;
    font-size: 1.05rem;
  }
  button {
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    border-radius: 10px;
    width: 34px;
    height: 34px;
    cursor: pointer;
    color: #334155;
  }
`;

const QrModalImage = styled.img`
  width: min(86vw, 340px);
  height: min(86vw, 340px);
  display: block;
  margin: 8px auto 10px;
  border-radius: 14px;
  border: 1px solid #cbd5e1;
  background: #fff;
`;

const QrInfo = styled.div`
  font-size: 13px;
  color: #475569;
  font-weight: 600;
`;

const QrModalActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const Card = styled.div`
  background: ${(p) => (p.$active ? "#eef2ff" : "#fff")};
  border: 1px solid ${(p) => (p.$active ? "#2563eb" : "#e5e7eb")};
  border-radius: 16px;
  padding: 12px;
  display: grid;
  gap: 8px;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease;
  &:hover {
    transform: translateY(-2px);
  }
`;

const Preview = styled.div`
  height: 110px;
  border-radius: 12px;
`;

const CardTitle = styled.div`
  font-weight: 800;
  font-size: 1rem;
`;

const CardSub = styled.div`
  color: #64748b;
`;

const ActivePill = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #1d4ed8;
  background: #dbeafe;
  padding: 4px 8px;
  border-radius: 999px;
  width: fit-content;
`;

const PrimaryBtn = styled.button`
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 12px;
  padding: 12px 16px;
  font-weight: 800;
  cursor: pointer;
  width: fit-content;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
